import { TrendingTag } from '@app/data/clients/monolithClients/trending-search-topics/types';
import { Shop_IaEntityCopy } from '@app/data/shop-bff';
import { isNotNull } from '@app/types/utils';
import { Box, Image, Text } from '@redbubble/design-system';
import { useViewportWidth } from '@redbubble/next/hooks';
import {
  defineMessages,
  FormattedMessage,
  IntlShape,
  useIntl,
} from '@redbubble/next/intl';
import { DeviceType } from '@redbubble/next/types';
import { uniqueId } from 'lodash';
import { ReactNode } from 'react';
import { IaEntitiesCopy, Work } from 'types/shop-bff/types';
import logo from './logo-google.svg';
import { TagsList } from './TagsList';
import styles from './TagsSection.module.scss';
import {
  allProductsTags,
  categoryPagePath,
  convertTrendingTagToTag,
  productTagList,
} from './utils';

const messages = defineMessages({
  productTagsHeader: { defaultMessage: 'Related Tags' },
  allProductTagsTitle: { defaultMessage: 'All Product Tags' },
  trendingSearchTopics: { defaultMessage: 'Trending topics' },
  translatedByGoogle: { defaultMessage: 'Translated by Google' },
  a11yAllProductsTagTitleDesc: {
    defaultMessage:
      'This section provides a collection of tags that each link to a search for any products that relate to the tag.',
  },
  a11yCurrentProductsTagTitleDesc: {
    defaultMessage:
      'This section provides a collection of tags that relate to the {product} on this page. These tags each link to a search for the {productPlural} that relate to the tag.',
  },
});

interface CurrentProductTitleProps {
  iaEntityCopy: Shop_IaEntityCopy;
  locale: string;
}

export const CurrentProductTitle: React.FC<CurrentProductTitleProps> = ({
  iaEntityCopy,
  locale,
}) => {
  const categoryHref = categoryPagePath(iaEntityCopy.canonicalForm, locale);
  return (
    <FormattedMessage
      defaultMessage="{tagTitleText} Tags"
      values={{
        tagTitleText: (
          <a href={categoryHref}>{iaEntityCopy.pluralDisplayName}</a>
        ),
      }}
    />
  );
};

interface TagsTitleProps {
  children: ReactNode;
  description: string;
}

export const TagsTitle: React.FC<TagsTitleProps> = ({
  children,
  description,
}) => {
  const describedbyId = uniqueId('tags-title-description');

  return (
    <>
      <Text
        type="display5"
        aria-describedby={describedbyId}
        element="h3"
        className={styles.title}
      >
        {children}
      </Text>

      <p
        className={styles.hiddenDescription}
        id={describedbyId}
        data-testid={describedbyId}
      >
        {description}
      </p>
    </>
  );
};

interface MachineTranslatedSectionProps {
  currentLocale: string;
  originalLocale: string;
  intl: IntlShape;
}

export const MachineTranslatedSection: React.FC<
  MachineTranslatedSectionProps
> = ({ currentLocale, originalLocale, intl }) => {
  const langAttribute = `${currentLocale}-x-mtfrom-${originalLocale}`;
  return (
    <Box lang={langAttribute}>
      <Text type="body2" muted>
        <FormattedMessage
          defaultMessage="Translated by {GoogleLogo}"
          values={{
            GoogleLogo: (
              <Image
                src={logo.src}
                className={styles.logo}
                alt={intl.formatMessage(messages.translatedByGoogle)}
              />
            ),
          }}
        />
      </Text>
    </Box>
  );
};

interface TagsSectionHeaderProps {
  children: ReactNode;
  intl: IntlShape;
}

export const TagsSectionHeader: React.FC<TagsSectionHeaderProps> = ({
  children,
  intl,
}) => {
  return (
    <>
      <Text element="h2" className="clipped">
        {intl.formatMessage(messages.productTagsHeader)}
      </Text>
      {children}
    </>
  );
};

interface TagsSectionProps {
  locale: string;
  iaEntityCopy: IaEntitiesCopy | null;
  work?: Work;
  trendingSearchTags: TrendingTag[] | null;
}

export const TagsSection: React.FC<TagsSectionProps> = ({
  locale,
  iaEntityCopy,
  work,
  trendingSearchTags,
}) => {
  const intl = useIntl();
  const { isOrAbove } = useViewportWidth();
  const isTablet = isOrAbove(DeviceType.Tablet);
  if (!work?.tags?.length) return null;
  const { tags, linkBackTags, originalLocale, isMachineTranslated } = work;
  const allProducts = allProductsTags(tags, linkBackTags, locale);
  const trendingTags = trendingSearchTags
    ? trendingSearchTags.map(convertTrendingTagToTag).filter(isNotNull)
    : null;

  const currentProductTags = productTagList(tags, iaEntityCopy, locale);

  return (
    <TagsSectionHeader intl={intl}>
      {iaEntityCopy && currentProductTags && (
        <div data-testid="current-product-tags">
          <TagsTitle
            description={intl.formatMessage(
              messages.a11yCurrentProductsTagTitleDesc,
              {
                product: iaEntityCopy.label,
                productPlural: iaEntityCopy.pluralDisplayName,
              }
            )}
          >
            <CurrentProductTitle iaEntityCopy={iaEntityCopy} locale={locale} />
          </TagsTitle>

          <TagsList
            tags={currentProductTags}
            isTablet={isTablet}
            indexableTags={10}
          />
        </div>
      )}
      {allProducts && (
        <div data-testid="all-product-tags">
          <Box marginBottom="xs" />
          <TagsTitle
            description={intl.formatMessage(
              messages.a11yAllProductsTagTitleDesc
            )}
          >
            {intl.formatMessage(messages.allProductTagsTitle)}
          </TagsTitle>

          <TagsList tags={allProducts} isTablet={isTablet} indexableTags={10} />
        </div>
      )}
      {trendingTags && trendingTags.length > 0 && (
        <div data-testid="trending-tags">
          <Box marginBottom="xs" />
          <TagsTitle
            description={intl.formatMessage(
              messages.a11yAllProductsTagTitleDesc
            )}
          >
            {intl.formatMessage(messages.trendingSearchTopics)}
          </TagsTitle>

          <TagsList
            tags={trendingTags}
            isTablet={isTablet}
            indexableTags={20}
          />
        </div>
      )}
      {isMachineTranslated && originalLocale && locale !== originalLocale && (
        <MachineTranslatedSection
          currentLocale={locale}
          originalLocale={originalLocale}
          intl={intl}
        />
      )}
    </TagsSectionHeader>
  );
};
